import { getReplacedString } from '../../../../../helpers/getReplacedString';
import { factoryTypes } from './constants';
import { asIs, compactArray, convertToArray } from './utils';

export const createFilterBySearch = (value, options) => {
  const normalizedVal = getReplacedString(value);
  if (!normalizedVal) return asIs;

  function predicat(item) {
    const target = getReplacedString(options.extract(item));
    return target.includes(normalizedVal);
  }

  return (items = []) => items.filter(predicat);
};

export const createTreeFilterBySearch = (value, options) => {
  const normalizedVal = getReplacedString(value);
  if (!normalizedVal) return asIs;

  function match(str) {
    return getReplacedString(str).includes(normalizedVal);
  }

  function filterNode(node, level = 0, extractedValue) {
    if (!node || typeof node !== 'object') return null;

    if (Array.isArray(node))
      return node
        .map((el) => filterNode(el, level, extractedValue))
        .filter(Boolean);

    const { extract, childrenKey } = options[level];

    const _extractedValue = extractedValue
      ? [extractedValue, extract(node)].filter(Boolean).join('')
      : extract(node);

    if (match(_extractedValue)) return node;
    if (!childrenKey) return null;

    const children = filterNode(node[childrenKey], level + 1, _extractedValue);

    if (!children || (Array.isArray(children) && children.length === 0))
      return null;

    return { ...node, [childrenKey]: children };
  }

  return filterNode;
};

export const createFilterByParam = (value, options) => {
  const normalizedVal = compactArray(
    convertToArray(typeof value === 'string' ? value.trim() : value)
  );

  if (!normalizedVal.length) return asIs;

  function predicat(item) {
    const target = options.extract(item);

    return Array.isArray(target)
      ? target.some((v) => normalizedVal.includes(v))
      : normalizedVal.includes(target);
  }

  return (items = []) => items.filter(predicat);
};

export const createFilterByParams = (value = {}, options) => {
  const paramEntries = Object.entries(value);
  if (!paramEntries.length) return asIs;

  const filters = paramEntries.reduce((acc, [key, value]) => {
    acc.push(createFilterByParam(value, options[key]));
    return acc;
  }, []);

  return (items = []) =>
    items.length
      ? filters.reduce((result, filter) => filter(result), items)
      : items;
};

export const filterCreators = {
  [factoryTypes.bySearch]: createFilterBySearch,
  [factoryTypes.treeBySearch]: createTreeFilterBySearch,
  [factoryTypes.byParam]: createFilterByParam,
  [factoryTypes.byParams]: createFilterByParams,
};

export const createMixedFilter = (value = {}, options) => {
  const paramEntries = Object.entries(value);

  if (!paramEntries.length) return asIs;

  const filters = paramEntries.reduce((acc, [key, value]) => {
    const { type, extractor } = options[key];

    if (typeof filterCreators[type] === 'function') {
      acc.push(filterCreators[type](value, extractor));
    }

    return acc;
  }, []);

  return (items = []) =>
    items.length
      ? filters.reduce((result, filter) => filter(result), items)
      : items;
};
